// ModernBrushPanelTabPreference — Modern「ブラシ」パネルのタブ選択状態を per-user で
// localStorage に永続化する infrastructure 層ユーティリティ.
//
// 背景 (docs/_wip/modern-brush-panel-2026-08-17.md §14「タブ構成」consult 2026-08-18):
//   - チップ列 → 常設パネル (§1-§4) に続き、パネルの中身を**性格別タブ** (線画/塗り/
//     質感/マイブラシ) に置き換える (§14.1-§14.3)。
//   - **タブ選択状態は `WorkspaceState` に入れない** (§14.3、§6 の workspace migration に
//     触れないため)。既存の `modernBrushPanelViewPreference.ts` (list/grid 切替) と同じ
//     localStorage-only の流儀を踏襲する。
//   - ただし本モジュールの `get()` は「永続値が無い/不正なら `null`」を返す (list/grid の
//     `DEFAULT_BRUSH_PANEL_VIEW` 直接返却とは異なる)。呼び出し側 (`ModernBrushPanel`) は
//     **「現在の pen が属するタブ」を永続値より優先**する (= 選択中ブラシがどのタブにも
//     無い状態を防ぐ、§14.3「選択中ブラシを含むタブを自動で開く」)。pen が
//     どのタブにも属さない (例: 消しゴム選択中) ときだけ永続値にフォールバックし、それも
//     無ければ既定タブ (線画) にする。この優先順位判断はコンポーネント側の責務なので、
//     本モジュールは単純な read/write だけを提供する。
//
// 設計は `stabilizerPreference.ts` / `modernBrushPanelViewPreference.ts` と同流儀
// (StorageLike 抽象 + sentinel probe)。
//
// Tests: modernBrushPanelTabPreference.test.ts (storage-injection style)。

/** ブラシパネルのタブ ID (§14.2: 実装区分でなく表現の性格で切る、4 つで上限 §14.5)。 */
export type BrushPanelTabId = 'line' | 'fill' | 'texture' | 'myBrush';

/** 全タブ ID (安定列、既定タブ判定・巡回に使う)。 */
export const ALL_BRUSH_PANEL_TAB_IDS: ReadonlyArray<BrushPanelTabId> = [
  'line',
  'fill',
  'texture',
  'myBrush',
];

/** 既定タブ (= 線画、既定ペン `brush` を含むため、§14.3)。 */
export const DEFAULT_BRUSH_PANEL_TAB: BrushPanelTabId = 'line';

/** localStorage キー: ブラシパネルのタブ選択状態. */
export const BRUSH_PANEL_TAB_KEY = 'oekaki:modern-brush-panel-tab';

/** 文字列が有効な BrushPanelTabId か判定する type guard. */
export const isBrushPanelTabId = (v: unknown): v is BrushPanelTabId =>
  typeof v === 'string' && (ALL_BRUSH_PANEL_TAB_IDS as readonly string[]).includes(v);

/**
 * Minimal Storage shape this module uses (modernBrushPanelViewPreference.ts と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ModernBrushPanelTabPreferenceStore {
  /** 永続化済みタブ選択を読み出し. 不在 / 不正 / storage 不可 → `null`. */
  get(): BrushPanelTabId | null;
  /** タブ選択を保存. storage 不可なら no-op (例外を throw しない). */
  set(tab: BrushPanelTabId): void;
  /** key を削除. テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * modernBrushPanelViewPreference.ts の probe と同じ手順 (sentinel write + readback で
 * Safari private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:modern-brush-panel-tab__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateModernBrushPanelTabPreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getter always returns `null`, setter silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readBrushPanelTab = (storage: StorageLike): BrushPanelTabId | null => {
  try {
    const raw = storage.getItem(BRUSH_PANEL_TAB_KEY);
    if (raw === null) return null;
    return isBrushPanelTabId(raw) ? raw : null;
  } catch {
    return null;
  }
};

const writeBrushPanelTab = (storage: StorageLike, tab: BrushPanelTabId): void => {
  try {
    storage.setItem(BRUSH_PANEL_TAB_KEY, tab);
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `ModernBrushPanelTabPreferenceStore` のインスタンスを構築. storage が unavailable な
 * 環境 (SSR / Safari private browsing 厳格モード) でも getter は `null` を返すだけで、
 * setter は no-op (= 例外で UI が壊れることはない).
 */
export const createModernBrushPanelTabPreferenceStore = (
  opts: CreateModernBrushPanelTabPreferenceStoreOptions = {},
): ModernBrushPanelTabPreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return null;
      return readBrushPanelTab(storage);
    },
    set(tab) {
      if (storage === null) return;
      writeBrushPanelTab(storage, tab);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(BRUSH_PANEL_TAB_KEY);
      } catch {
        // ignore
      }
    },
  };
};
